"use client"

import { ACCOUNT_DISABLED_EVENT, advanceAccessSession, configureAccessNavigation, setAccessIdentity, setSubscriptionAccess, SUBSCRIPTION_ACCESS_EVENT } from './auth/access';

import ApplicationAccessGate from './auth/ApplicationAccessGate';
import type { Settings as LayoutSettings } from "@ant-design/pro-components"
import type { RunTimeLayoutConfig } from "@umijs/max"
import { history, Link, Sentry, useDispatch, useLocation } from "@umijs/max"
import defaultSettings from "../config/defaultSettings"
import { errorConfig } from "./requestErrorConfig"
import { analytics } from "./analytics/client"
import { AnalyticsObserver } from "./analytics/observer"
import { analyticsDiagnostics } from './analytics/diagnostics';
import { analyticsConfig } from './featureAnalytics';
import { ANALYTICS_V2_VERSION } from './analytics/contract-v2';
import { consumeNavigationHandoff, takeNavigationHandoffToken, NavigationContext } from './analytics/navigation';
import { useEffect, useState, useRef } from "react"
import { currentUser, logout } from "./pages/User/Login/service"
import { DollarOutlined, UserOutlined, CustomerServiceOutlined, LogoutOutlined } from "@ant-design/icons"
import { Button, ConfigProvider, Flex, Menu, message, Result, Space } from "antd"
import AydentifyAnimation from "./components/animated_logo"
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useMediaQuery } from "react-responsive"
import HeaderCreditsMeter from "./components/CreditsMeter/HeaderCreditsMeter"
import AccessibilityMagnifier from "./components/AccessibilityMagnifier"
import {
  canAccessAgentPage,
  canAccessAccountsPage,
  canAccessMapPage,
  canAccessProductSurfaces,
  isPeopleOnlyAllowedPath,
  isPeopleOnlyUser,
} from "./utils/userRoles"

// Declare Zoho SalesIQ types
declare global {
  interface Window {
    $zoho?: {
      salesiq?: {
        ready: (callback: () => void) => void;
        visitor?: {
          name: (name: string) => void;
          email: (email: string) => void;
        };
        privacy?: {
          updateCookieConsent: (consentCategories: ('analytics' | 'performance')[]) => void;
          getCookieList?: () => Promise<string[]>;
        };
      };
    };

  }
}


// For Firebase JS SDK v7.20.0 and later, measurementId is optional
// const firebaseConfig = {
//   apiKey: "AIzaSyAiG9qdW_tnXjyyqzobxEcaeBmD64BZJQg",
//   authDomain: "scout-e78e3.firebaseapp.com",
//   projectId: "scout-e78e3",
//   storageBucket: "scout-e78e3.appspot.com",
//   messagingSenderId: "506508788463",
//   appId: "1:506508788463:web:fd5e170891958dcf27d11a",
//   measurementId: "G-9YLCJ9X56N",
// }

const loginPath = "/user/login"
configureAccessNavigation((path) => history.replace(path));

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>
  currentUser?: any
  loading?: boolean
  authUnavailable?: boolean
  analyticsNavigationContext?: NavigationContext
  fetchUserInfo?: () => Promise<any>
}> {
  const analyticsHandoffToken = takeNavigationHandoffToken();
  let analyticsNavigationContext: NavigationContext | undefined;
  analyticsDiagnostics.start({
    environment: analyticsConfig.environment,
    release_id: analyticsConfig.releaseVersion || 'unversioned',
    schema_version: ANALYTICS_V2_VERSION,
    enabled: analyticsConfig.enabled,
    server_zone: analyticsConfig.serverZone,
    project_fingerprint: analyticsConfig.projectFingerprint,
  }, (report) => {
    if (!Sentry.getClient()) throw new Error('Diagnostics transport unavailable');
    Sentry.withScope((scope) => {
      scope.clear();
      scope.addEventProcessor((event) => ({
        ...event, user: undefined, request: undefined, breadcrumbs: undefined,
      }));
      Sentry.captureEvent({
        message: 'analytics_health', level: 'info', fingerprint: ['analytics_health'],
        tags: {
          source: 'frontend', environment: report.context.environment,
          release_version: report.context.release_id,
          schema_version: String(report.context.schema_version),
        },
        contexts: { analytics_health: { ...report } },
      });
    });
  });
  void analytics.initialize();
  let authUnavailable = false;
  const fetchUserInfo = async () => {
    try {
      const msg = await currentUser()
      authUnavailable = false;
      setAccessIdentity((msg as any)?.id);
      setSubscriptionAccess((msg as any)?.application_access, false);
      analytics.setUser(msg as any)
      if (analyticsHandoffToken) analyticsNavigationContext = await consumeNavigationHandoff(analyticsHandoffToken).catch(() => undefined);
      return msg
    } catch (error: any) {
      if (error?.response?.status === 503) {
        authUnavailable = true;
        throw error;
      }
      if (error?.response?.status === 401) analytics.reset()
      if (error?.response?.data?.code !== 'ACCOUNT_DISABLED') history.push(loginPath)
      // Login/phone-verification continuations must not report success or
      // navigate back into the app after currentUser has rejected the session.
      throw error;
    }
  }

  const { location } = history
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo().catch(() => undefined)
    return {
      fetchUserInfo,
      currentUser,
      authUnavailable,
      analyticsNavigationContext,
      settings: defaultSettings as Partial<LayoutSettings>,
    }
  }
  analytics.setUser(undefined)
  return {
    fetchUserInfo,
    analyticsNavigationContext,
    settings: defaultSettings as Partial<LayoutSettings>,
  }
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }: any) => {
  const { currentUser } = initialState
  const peopleOnly = isPeopleOnlyUser(currentUser)
  const dispatch = useDispatch()
  useEffect(() => {
    const blocked = () => {
      dispatch({ type: 'profile/resetInterestLists' });
      setInitialState((state: any) => ({ ...state, currentUser: undefined, authUnavailable: false, analyticsNavigationContext: undefined }));
    };
    const subscriptionChanged = (event: Event) => {
      const access = (event as CustomEvent).detail;
      if (!access.allowed) {
        for (const type of ['search/resetAll', 'people/resetAll', 'packaging_search/resetAll', 'profile/resetInterestLists', 'subscription/reset']) dispatch({ type });
      }
      setInitialState((state: any) => ({ ...state, currentUser: state.currentUser ? { ...state.currentUser, application_access: access } : undefined }));
    };
    window.addEventListener(ACCOUNT_DISABLED_EVENT, blocked);
    window.addEventListener(SUBSCRIPTION_ACCESS_EVENT, subscriptionChanged);
    return () => {
      window.removeEventListener(ACCOUNT_DISABLED_EVENT, blocked);
      window.removeEventListener(SUBSCRIPTION_ACCESS_EVENT, subscriptionChanged);
    };
  }, [dispatch, setInitialState]);
  const isTabletOrMobile = useMediaQuery({ maxWidth: 768 });

  const [isHovered, setIsHovered] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const [loading, setLoading] = useState(false)
  const location = useLocation()

  // Track previous path to detect navigation away from search pages
  const prevPathRef = useRef<string>(location.pathname)

  // Effect to clear search data when navigating away from search pages
  useEffect(() => {
    const currentPath = location.pathname
    const prevPath = prevPathRef.current

    // Product search UI is mounted at /products; older links use /search
    const isProductSearchPath = (p: string) => p === '/search' || p === '/products'

    const wasOnSearchPage = isProductSearchPath(prevPath)
    const isSearchPage = isProductSearchPath(currentPath)

    // If returning to search page from another page, clear search data
    if (isSearchPage && !wasOnSearchPage) {
      dispatch({ type: 'search/resetAll' })
      dispatch({ type: 'people/resetAll' })
      dispatch({ type: 'packaging_search/resetAll' })
    }

    // Update the previous path ref
    prevPathRef.current = currentPath
  }, [location.pathname, dispatch])


  useEffect(() => {
    if (currentUser && currentUser.application_access?.allowed !== false) {
      dispatch({
        type: "subscription/getCurrentPlan",
      })
    }
  }, [currentUser, dispatch])

  useEffect(() => {
    if (!isTabletOrMobile) {
      const interval = setInterval(() => {
        const sider = document.querySelector(".ant-layout-sider")
        if (sider) {
          const hovered = sider.matches(":hover")
          if (hovered && !isHovered) {
            setIsHovered(true)
            setCollapsed(false)
          } else if (!hovered && isHovered) {
            setIsHovered(false)
            setCollapsed(true)
          }
        }
      }, 100)

      return () => clearInterval(interval)
    }
  }, [isHovered, isTabletOrMobile])

  // Apply desktop-only layout styles conditionally
  useEffect(() => {
    const layoutElement = document.querySelector('.ant-layout > div:nth-child(1)')

    if (layoutElement) {
      if (!isTabletOrMobile) {
        // Apply desktop styles
        layoutElement.classList.add('desktop-layout-sider')
      } else {
        // Remove desktop styles for mobile/tablet
        layoutElement.classList.remove('desktop-layout-sider')
      }
    }
  }, [isTabletOrMobile])

  const handleLogout = async () => {
    try {
      setLoading(true)

      // Clean up Zoho SalesIQ if needed - remove script
      const zsiqScript = document.getElementById('zsiqscript');
      if (zsiqScript) {
        zsiqScript.remove();
      }

      // Remove any Zoho elements
      document.querySelectorAll('[id^="zsiq_"]').forEach(el => el.remove());

      await logout()
      advanceAccessSession();
      setAccessIdentity(undefined);
      analytics.reset()
      dispatch({ type: 'profile/resetInterestLists' })
      setInitialState((s: any) => ({ ...s, currentUser: undefined, analyticsNavigationContext: undefined }))
      history.push(loginPath)
    } catch (error: any) {
      message.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const bottomMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: currentUser?.fullname,
      onClick: () => history.push("/profile"),
    },
    {
      key: "support",
      icon: <CustomerServiceOutlined />,
      label: "Support",
      onClick: () => history.push("/support"),
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log Out",
      onClick: handleLogout,
      disabled: loading,
    },
  ].filter((item) => !peopleOnly || item.key === 'profile' || item.key === 'logout')

  // if (currentUser?.current_subscription?.name !== 'Enterprise') {
  //   // add to the beginning of the array
  //   bottomMenuItems.unshift({
  //     key: "pricing",
  //     icon: <DollarOutlined />,
  //     label: "Pricing",
  //     onClick: () => history.push("/pricing"),
  //   })
  // }

  return {
    token: {
      bgLayout: "#fff",
      pageContainer: {
        paddingBlockPageContainerContent: 0,
        paddingInlinePageContainerContent: 0,
      },
    },
    rightContentRender(headerProps, dom, props) {
      if (isTabletOrMobile) {
        return (
          <Space>
            <HeaderCreditsMeter />
          </Space>
        )
      }
    },
    onCollapse: (collapsed: boolean) => {
      setCollapsed(collapsed)
    },
    siderWidth: 220,
    title: false,
    collapsed: collapsed,
    defaultCollapsed: true,
    breakpoint: false,
    collapsedButtonRender: false,
    logo: <AydentifyAnimation isExpanded={!collapsed} />,
    logoStyle: {
      backgroundColor: "#FFE500",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "68px", // Adjust this value as needed
    },
    menuContentRender: (props, defaultDom) => {
      const menuData = props.menuData?.filter((item: any) => {
        if (peopleOnly) {
          return item.path === '/people' || item.path === '/lists'
        }
        if (item.path === '/agent') {
          return canAccessAgentPage(currentUser)
        }
        if (item.path === '/products' || item.path === '/search') {
          return canAccessProductSurfaces(currentUser)
        }
        if (item.path === '/accounts') {
          return canAccessAccountsPage(currentUser)
        }
        if (item.path === '/map') {
          return canAccessMapPage(currentUser)
        }
        return true
      })

      return (
        <Flex vertical justify="space-between" style={{ height: "100%" }}>
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            items={menuData?.map((item: any) => ({
              key: item.path,
              icon: item.icon,
              label: <Link to={item.path}>{item.name}</Link>,
              style: {
                paddingLeft: 12,
                marginLeft: 4,
              },
            }))}
            selectedKeys={[location.pathname]}
          />
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            items={bottomMenuItems.map((item) => ({
              ...item,
              label: item.key === 'logout' ? item.label : <Link to={item.key}>{item.label}</Link>,
              style: {
                paddingLeft: 12,
                marginLeft: 4,
              },
            }))}
            selectedKeys={[location.pathname.replace("/", "")]}
            style={{
              borderTop: "1px solid #f0f0f0",
            }}
          />
        </Flex>
      )
    },
    onPageChange: () => {
      const { location } = history

      if (!initialState?.currentUser && !initialState?.authUnavailable && location.pathname !== loginPath) {
        history.push(loginPath)
        return
      }

      if (currentUser?.application_access?.allowed === false) {
        if (location.pathname !== '/subscription-required') history.replace('/subscription-required');
        return;
      }

      if (peopleOnly && location.pathname !== '/subscription-required' && !isPeopleOnlyAllowedPath(location.pathname)) {
        history.replace('/people')
      }
    },

    childrenRender: (children: any) => {
      return (
        <ConfigProvider
          theme={{
            components: {
              Select: {
                optionSelectedBg: '#989898',
              },
            },
          }}
        >
          <div style={{ paddingLeft: isTabletOrMobile ? 0 : 64 }}>
            {initialState?.authUnavailable ? (
              <Result status="warning" title="Access could not be verified" subTitle="Please try again in a moment."
                extra={<Button onClick={() => window.location.reload()}>Retry</Button>} />
            ) : <ApplicationAccessGate currentUser={currentUser}>{children}</ApplicationAccessGate>}
          </div>
          {/* {initialState?.currentUser && <FloatingProductChat />} */}
        </ConfigProvider>
      )
    },
    ...initialState?.settings,
    ...(currentUser?.application_access?.allowed === false ? { menuRender: false, headerRender: false, footerRender: false } : {}),
  }
}

export const request = {
  ...errorConfig,
}

export function rootContainer(container: React.ReactNode) {
  return (
    <GoogleOAuthProvider clientId={'1027052181595-lqkskr08l3qk3j222oc6be1tn7438fkn.apps.googleusercontent.com'}>
      <AnalyticsObserver>
        {container}
        <AccessibilityMagnifier />
      </AnalyticsObserver>
    </GoogleOAuthProvider>
  );
}
